import React, { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import Select from "react-select"
import {
  Box,
  Grid,
  Stack,
  TextField,
  Typography,
  Card,
  IconButton,
  FormControlLabel,
  FormControl,
  CircularProgress,
  Checkbox,
} from '@mui/material';
import { grey } from '@mui/material/colors';
// icons
import AngleRightIcon from "common/customIcons/angle_right";
// utils and helpers
import { numberOnly, formatTimestampToWithoutTime } from "utilities/common";
import { validateDistributionProduct } from "validations/hyperwolf/distributionmanagement";
import { openSnackbar } from "../../../../redux/slices/common";
import { addDistributionProduct, getBoxes, getDistributionDrivers, getDistributionKitRegions, getDistributionProducts } from "../../../../redux/slices/hyperwolf/inventoryDistribution";
// custom components
import CommonLayout from "layouts/DashboardLayout";
import MDButton from 'components/MDButton';
import { showToastMsg } from "../addRegionKit";

const reactSelectStyles = {
  control: base => ({
    ...base,
    borderRadius: "10rem !important",
    backgroundColor: "transparent !important",
    border: "1px solid #adb5bd !important",
    "&:hover": {
      border: "1px solid #656575 !important"
    }
  }),
  menu: base => ({
    ...base,
    fontSize: "14px"
  })
}

const AddProductDistribution = () => {
  const { distributionId } = useParams()
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams()
  const { regionId, distributionDate } = Object.fromEntries(searchParams.entries())

  const [details, setDetails] = useState({
    regionIds: [regionId],
    distributionId: distributionId,
    boxId: "",
    productId: "",
    qtyPerDriver: 0,
    driverIds: []
  });
  const [regionsList, setRegionsList] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [boxes, setBoxes] = useState([]);
  const [products, setProducts] = useState([]);
  const [errors, setErrors] = useState({});
  const [isProductsLoading, setIsProductsLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleRegionsList = async () => {
    try {
      const response = await dispatch(getDistributionKitRegions({ distributionId, limit: 100 })).unwrap()
      if (response?.data) {
        setRegionsList(response?.data)
      }
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error?.message || error
      dispatch(openSnackbar({ content: errorMsg, color: "error", icon: "warning", }));
    }
  };

  const handleBoxesList = async () => {
    try {
      const response = await dispatch(getBoxes({ limit: 200 })).unwrap();
      if (response?.data) {
        setBoxes(response?.data || [])
      }
    }
    catch (error) {
      const errorMsg = error?.response?.data?.message || error?.message || error
      dispatch(openSnackbar({ content: errorMsg, color: "error", icon: "warning", }));
    }
  }

  const handleProducts = async (filters) => {
    setIsProductsLoading(true);
    try {
      const response = await dispatch(getDistributionProducts({ ...filters, distributionId })).unwrap();
      if (response?.data) {
        setProducts(response?.data || [])
      }
    }
    catch (error) {
      showToastMsg(error, "error")
    } finally {
      setIsProductsLoading(false)
    }
  }

  const handleDriversList = async (filters) => {
    try {
      const response = await dispatch(getDistributionDrivers({ limit: 200, ...filters })).unwrap();
      if (response?.data) {
        const sortedDrivers = response?.data?.sort((a, b) => a.fleetName.localeCompare(b.fleetName)) || []
        setDrivers(sortedDrivers)
        // Auto-select all drivers by default
        selectAllDrivers(sortedDrivers)
      }
    }
    catch (error) {
      const errorMsg = error?.response?.data?.message || error?.message || error
      dispatch(openSnackbar({ content: errorMsg, color: "error", icon: "warning", }));
    }
  };

  // Separate function to select all drivers
  const selectAllDrivers = (driversList = drivers) => {
    if (driversList && driversList.length > 0) {
      const allDriverIds = driversList.map(driver => driver._id)
      setDetails(prev => ({ ...prev, driverIds: allDriverIds }))
    }
  };

  // Function to manually select/deselect all drivers
  const handleSelectAllToggle = () => {
    if (details.driverIds.length === drivers.length) {
      // If all drivers are selected, deselect all
      setDetails(prev => ({ ...prev, driverIds: [] }))
    } else {
      // Select all drivers
      selectAllDrivers()
    }
  };

  const handleChange = (e) => {
    const { name, value, checked } = e.target;

    if (name === "productId") {
      const { productId, retailValue, totalQuantity, availableQuantity } = value || {}
      const details = { productId, retailValue, totalQuantity, availableQuantity }

      setDetails(prev => ({ ...prev, ...details }));
    } else if (name === "regionId") {
      setDetails(prev => {
        const newRegionIds = checked ? [...prev.regionIds, value] : prev.regionIds.filter(id => id !== value);
        return { ...prev, regionIds: newRegionIds, ...(newRegionIds.length === 0 ? { driverIds: [] } : {}) };
      });
    } else if (name === "qtyPerDriver") {
      setDetails(prev => ({ ...prev, qtyPerDriver: numberOnly(value) }));
    } else {
      setDetails(prev => ({ ...prev, [name]: value }));
    }
  }
  const handleReactSelectChange = (name, value) => {
    setDetails(prev => ({ ...prev, [name]: value }));
  }


  const handleSubmit = async () => {
    if (isLoading) return;
    setIsLoading(true);

    try {
      const { isValid, myErrors } = validateDistributionProduct(details);
      if (!isValid) {
        setErrors(myErrors);
        return;
      } else {
        setErrors({});
      }

      const response = await dispatch(addDistributionProduct({ ...details })).unwrap();
      if (response?.data) {
        showToastMsg(response?.message || "Success", "success")
        navigate(-1)
      }
    }
    catch (error) {
      showToastMsg(error, 'error')
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    handleRegionsList();
    handleBoxesList()
  }, []);

  useEffect(() => {
    if (details.regionIds && details.regionIds?.length > 0) {
      handleDriversList({ regionIds: details?.regionIds?.join(",") });
    }
  }, [details.regionIds])

  useEffect(() => {
    if (details.regionIds && details.regionIds?.length > 0 && details?.boxId) {
      handleProducts({ regionIds: details.regionIds, boxId: details?.boxId });
    }
  }, [details.regionIds, details?.boxId])

  return (
    <CommonLayout>
      <div className='w-100 products-distribution-sec'>
        <Box p={{ xs: 2, sm: 3, lg: 4 }} className="cus-region-kit">
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
            gap={2}
            mb={2}
          >
            <Box display="flex" alignItems="center" color="text.main" lineHeight={1}>
              <IconButton onClick={() => navigate(-1)}>
                <AngleRightIcon fontSize="14px" />
              </IconButton>
              <Typography variant="body2" fontWeight="semibold" ml={0.75}>
                Distribution ({formatTimestampToWithoutTime(Number(distributionDate))})
              </Typography>
            </Box>
          </Stack>

          <Card sx={{ p: { xs: 2, md: 3 }, mb: 2, borderRadius: 2.5, boxShadow: "none" }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h5" fontWeight="semibold">Add Product</Typography>
              </Grid>

              {/* Regions */}
              <Grid item xs={12}>
                <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>All Drivers In Region</Typography>
                <FormControl className="radio-tabs">
                  {
                    regionsList?.map(region => (
                      <FormControlLabel
                        key={region.regionId}
                        control={<Checkbox />}
                        name="regionId"
                        label={region?.regionName}
                        value={region.regionId}
                        checked={details.regionIds.includes(region.regionId)}
                        onChange={handleChange}
                      />
                    ))
                  }
                </FormControl>
                {errors?.regionIds && <p className="error">{errors?.regionIds}</p>}
              </Grid>

              {/* Box */}
              <Grid item xs={12}>
                <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Box</Typography>
                <Select
                  components={{
                    IndicatorSeparator: null
                  }}
                  menuPortalTarget={document.body}
                  className='cus-react-select'
                  styles={reactSelectStyles}
                  name="boxId"
                  placeholder='Select Box'
                  options={boxes || []}
                  getOptionLabel={option => option.name}
                  getOptionValue={option => option._id}
                  value={boxes?.find(box => box._id === details.boxId)}
                  onChange={({ _id }) => handleReactSelectChange("boxId", _id)}
                />
                {errors?.boxId && <p className="error">{errors?.boxId}</p>}
              </Grid>

              {/* Products */}
              <Grid item xs={12}>
                <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Product</Typography>
                {isProductsLoading ?
                  <TextField
                    fullWidth
                    variant="outlined"
                    className="form-input"
                    placeholder="Loading..."
                    disabled={isProductsLoading}
                  />
                  : <Select
                    components={{
                      IndicatorSeparator: null
                    }}
                    menuPortalTarget={document.body}
                    className='cus-react-select'
                    styles={reactSelectStyles}
                    isDisabled={!details.boxId}
                    placeholder={!details.boxId ? "Select Box to see products" : "Select Product"}
                    noOptionsMessage={() => "No Products Found"}
                    options={products || []}
                    name="productId"
                    value={products?.find(product => product.productId === details.productId)}
                    getOptionLabel={option => `${option.adminProductName} ${option?.brandName || ""} ${option?.subCategoryName || ""}`}
                    getOptionValue={option => option.productId}
                    formatOptionLabel={(option, { context }) => {
                      if (context === "value") {
                        return option.adminProductName;
                      }
                      return (
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {option.adminProductName}
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center">
                            {option?.brandName && (
                              <Typography variant="caption" color="textSecondary" sx={{ fontSize: '10px', background: '#f0f0f0', px: 1, borderRadius: '4px' }}>
                                Brand: {option.brandName}
                              </Typography>
                            )}
                            {option?.subCategoryName && (
                              <Typography variant="caption" color="textSecondary" sx={{ fontSize: '10px', background: '#f0f0f0', px: 1, borderRadius: '4px' }}>
                                Category: {option.subCategoryName}
                              </Typography>
                            )}
                          </Stack>
                        </Box>
                      );
                    }}
                    onChange={({ productId }) => handleReactSelectChange("productId", productId)}
                  />
                }
                {errors?.productId && <p className="error">{errors?.productId}</p>}
              </Grid>

              {/* Drivers */}
              <Grid item xs={12}>
                <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Region Drivers</Typography>
                {drivers.length > 0 && (
                  details.regionIds?.length ? <FormControlLabel
                    control={
                      <Checkbox
                        checked={details.driverIds.length === drivers.length && drivers.length > 0}
                        indeterminate={details.driverIds.length > 0 && details.driverIds.length < drivers.length}
                        onChange={handleSelectAllToggle}
                      />
                    }
                    label={`Select All (${drivers.length} drivers)`}
                    sx={{ mb: 1 }}
                  />
                  :""
                )}
                <Select
                  components={{
                    IndicatorSeparator: null
                  }}
                  menuPortalTarget={document.body}
                  className='cus-react-select'
                  styles={{
                    ...reactSelectStyles,
                    multiValue: base => ({
                      ...base,
                      borderRadius: "10rem",
                      paddingInline: 6,
                      backgroundColor: "#00000014"
                    })
                  }}
                  isMulti
                  closeMenuOnSelect={false}
                  isDisabled={!details.regionIds?.length}
                  placeholder={!details.regionIds?.length ? "Select Region First" : "Select drivers"}
                  options={drivers || []}
                  name="driverIds"
                  value={drivers?.filter(driver => details.driverIds?.includes(driver._id))}
                  getOptionLabel={option => option.fleetName}
                  getOptionValue={option => option._id}
                  onChange={(selected) => {
                    const ids = selected.map(driver => driver._id)
                    handleReactSelectChange("driverIds", ids)
                  }}
                />
                {errors?.driverIds && <p className="error">{errors?.driverIds}</p>}
              </Grid>

              <Grid item xs={12}>
                <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Template Retail Value</Typography>
                <div className="position-relative overflow-hidden">
                  <span className="dolar-sign">$</span>
                  <TextField
                    label=""
                    fullWidth
                    variant="outlined"
                    className="form-input"
                    name="retailValue"
                    disabled
                    value={details.retailValue}
                  />
                </div>
              </Grid>
              <Grid item xs={12}>
                <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Total Qty Of SKU In Safe</Typography>
                <div className="position-relative overflow-hidden">
                  <TextField
                    fullWidth
                    variant="outlined"
                    className="form-input"
                    disabled
                    value={details?.totalQuantity || 0}
                  />
                </div>
              </Grid>
              <Grid item xs={12}>
                <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Qty Per Driver</Typography>
                <div className="position-relative overflow-hidden">
                  <TextField
                    label=""
                    fullWidth
                    variant="outlined"
                    className="form-input"
                    name="qtyPerDriver"
                    value={details.qtyPerDriver}
                    onChange={handleChange}
                  />
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} className="plus-mins-sign">
                    <MDButton
                      type="button"
                      variant="contained"
                      color="dark"
                      onClick={() => {
                        if (!details?.qtyPerDriver) return;
                        setDetails((prev) => ({ ...prev, qtyPerDriver: prev.qtyPerDriver - 1 }))
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <rect width="24" height="24" rx="12" fill="#F2F3F7" />
                        <path d="M7 12H17" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </MDButton>
                    <MDButton
                      type="button"
                      variant="contained"
                      color="dark"
                      onClick={() => {
                        if (details?.qtyPerDriver > 100) return;
                        setDetails((prev) => ({ ...prev, qtyPerDriver: Number(prev.qtyPerDriver || 0) + 1 }))
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <rect width="24" height="24" rx="12" fill="#F2F3F7" />
                        <path d="M12 7V17M17 12H7" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </MDButton>
                  </Stack>
                </div>
                {errors?.qtyPerDriver && <p className="error">{errors?.qtyPerDriver}</p>}
              </Grid>
              <Grid item xs={12}>
                <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Total Driver Price</Typography>
                <div className="position-relative overflow-hidden">
                  <span className="dolar-sign">$</span>
                  <TextField
                    label=""
                    fullWidth
                    variant="outlined"
                    className="form-input"
                    disabled
                    value={details.pricePerPiece && details.qtyPerDriver ? (details.qtyPerDriver * details.pricePerPiece) : ""}
                  />
                </div>
              </Grid>
              <Grid item xs={12}>
                <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Available Quantity In Safe</Typography>
                <div className="position-relative overflow-hidden">
                  <TextField
                    label=""
                    fullWidth
                    variant="outlined"
                    className="form-input"
                    disabled
                    value={details.availableQuantity}
                  />
                </div>
              </Grid>
              <Grid item xs={12}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mt={1}>
                  <MDButton
                    sx={{ minWidth: "160px" }}
                    type="button"
                    variant="contained"
                    color="primary"
                    disabled={isLoading}
                    onClick={handleSubmit}
                  >
                    {isLoading ? <CircularProgress size={20} color="light" /> : "Save"}
                  </MDButton>
                  <MDButton
                    sx={{ minWidth: "160px" }}
                    type="button"
                    variant="outlined"
                    color="dark"
                    onClick={() => navigate(-1)}
                  >
                    Cancel
                  </MDButton>
                </Stack>
              </Grid>
            </Grid>
          </Card>
        </Box>
      </div>
    </CommonLayout>
  )
}

export default AddProductDistribution